import * as THREE from 'three';
import { STREAKS, STREAK_BY_ID } from './streaks/index.js';
import { spriteLayer, livingEnemies, disposeShared, updateTracers, tracers, clamp } from './streaks/common.js';

/**
 * Scorestreaks — earn, hold, deploy.
 *
 * ## The model
 *
 * Progress is **score**, not kills. A kill is worth what the match already pays
 * for it (100, or 150 for a headshot) plus a small multikill bonus, so a player
 * who shoots well climbs the ladder faster than a player who merely shoots often.
 * That is the single most important structural decision copied from the reference
 * list: with a flat per-kill counter, the top of the ladder is a stopwatch.
 *
 * Earned streaks go into a **tray of three**. They are selected with `4` and
 * deployed with `5`. When the tray is full the next earned streak is refused
 * rather than silently dropped, and the player is told — a streak vanishing into
 * nothing is the kind of thing that reads as a bug.
 *
 * ## Death policy — undeployed streaks are LOST
 *
 * Dying clears the tray. Banked score toward the *next* streak is kept, so a death
 * costs you what you were holding but never sends you back to zero. This is the
 * classic killstreak bargain and it is what makes "deploy it now or hold it for a
 * better moment" an actual decision. Assets you have *already* put on the map —
 * a turret, a helo, a burning corridor — keep running; they are out of your hands
 * by then.
 *
 * ## Where the work happens
 *
 * Two registrations. Deployed assets simulate on `'ai'`, after the enemies have
 * moved, so a turret is never a frame behind its target. The camera takeover
 * writes on `'fx'`, after the camera rig, because that is the only way to win the
 * frame without editing the rig. Both early-out to a few nanoseconds when nothing
 * is deployed.
 */

export const TUNING = {
  traySize: 3,

  /** Extra score for stacking kills inside the director's multikill window. */
  multikillBonus: 25,
  /** Score for a kill made *by* a deployed asset. Kept at parity with a rifle kill. */

  /** Undeployed streaks are lost on death. Banked progress is not. */
  loseTrayOnDeath: true,

  /**
   * Key codes, read straight off the input's pressed set. Nothing is added to the
   * input bindings table — that file belongs to another module, and `Digit4` /
   * `Digit5` are unclaimed there (the weapon slots stop at 3).
   *
   * Two keys rather than three direct-selects: with a tray of three, "cycle then
   * commit" is one key press in the common case (a single held streak) and never
   * more than three, and it leaves the deploy key in one fixed place instead of
   * moving depending on how many streaks you happen to be holding.
   */
  keyCycle: 'Digit4',
  keyDeploy: 'Digit5',

  /** Threat markers float this far above an enemy's feet. */
  markerHeight: 2.15,
  markerSize: 1.15,
  markerMax: 48,

  readyCalloutHold: 2.6,
};

export class Scorestreaks {
  constructor(ctx) {
    this.ctx = ctx;
    ctx.scorestreaks = this;

    this.score = 0;
    this.earnedTotal = 0;
    /** Index into STREAKS of the next rung. */
    this.rung = 0;
    /** [{ id, def }] — at most TUNING.traySize. */
    this.tray = [];
    this.selected = 0;

    /** Live instances, one per streak id, reused across deployments. */
    this.impls = new Map();
    this.active = [];

    /* ---- intel ---- */
    this._revealMode = 'none';
    this._revealTimer = 0;
    this._snapshot = new Set();
    this._continuous = false;
    this.perActor = new Map();
    ctx.intel = {
      /** True while every hostile should be considered located. */
      revealAll: false,
      /** 'none' | 'pulse' | 'uav' | 'gunship' */
      mode: 'none',
      /** Actor ids individually painted (helo spots, pulse snapshots). */
      ids: new Set(),
      /** Seconds of reveal remaining; 0 when nothing is up. */
      remaining: 0,
    };

    this._scratch = [];
    this._prompt = null;
    this.takeover = null;

    /* ---- profiling: exposed on __streaktest ---- */
    this.ms = 0;
    this.msPeak = 0;

    this._offs = [];
    this._bind();
    this._bindRequests();

    this._unregSim = ctx.engine.registerUpdate((dt, el) => this.update(dt, el), 'ai');
    this._unregLate = ctx.engine.registerUpdate((dt, el) => this.lateUpdate(dt, el), 'fx');

    this._warm();
    this._emitProgress();
    this._emitTray();
    this._exposeTestHooks();
  }

  /**
   * Build the pooled effects now rather than on the first streak that needs them.
   *
   * Measured: creating the fire layer and the tracer buffer inside the frame that
   * calls in a napalm strike costs 2.5 ms — buffer allocation plus a shader program
   * compile, at the exact moment nine explosions are also going off. Building them
   * here puts the allocation on the boot path, and leaving them *visible* for the
   * first frame means `renderer.compile()` in `main.js` — which runs immediately
   * after this constructor and only traverses visible objects — picks up the
   * programs too. They are hidden again before anything is rendered.
   */
  _warm() {
    const layers = [
      spriteLayer(this.ctx, 'threat', {
        count: TUNING.markerMax, size: TUNING.markerSize, additive: false, depthTest: false,
      }),
      spriteLayer(this.ctx, 'fire', { count: 260, size: 3.4, rgb: [3.1, 1.35, 0.42] }),
      spriteLayer(this.ctx, 'smoke', { count: 130, size: 3.6, additive: false }),
      spriteLayer(this.ctx, 'pkgsmoke', {
        texture: 'smoke', count: 40, size: 2.2, color: 0xffb27a, additive: false,
      }),
    ];
    const beams = tracers(this.ctx);
    for (const l of layers) l.points.visible = true;
    beams.mesh.visible = true;

    const off = this.ctx.engine.registerUpdate(() => {
      for (const l of layers) l.points.visible = false;
      beams.mesh.visible = false;
      off();
    }, 'ui');
  }

  /* ------------------------------- lifecycle ------------------------------- */

  _bind() {
    const ev = this.ctx.events;
    const on = (n, f) => this._offs.push(ev.on(n, f));

    on('player:death', () => {
      if (TUNING.loseTrayOnDeath && this.tray.length) {
        const lost = this.tray.length;
        this.tray.length = 0;
        this.selected = 0;
        this._emitTray();
        ev.emit('streak:lost', { count: lost });
      }
      this._abortTakeover();
    });

    on('game:state', ({ state }) => {
      if (state === 'menu' || state === 'won' || state === 'dead') this._abortTakeover();
    });
  }

  /** A takeover streak must never survive a state change it did not cause. */
  _abortTakeover() {
    if (!this.takeover) return;
    const impl = this.impls.get(this.takeover);
    impl?.end?.();
    const i = this.active.findIndex((a) => a.id === this.takeover);
    if (i !== -1) this._retire(i);
  }

  /* --------------------------------- score --------------------------------- */

  /**
   * Award streak score. Called by the GameDirector on every kill, which is the
   * only module that knows what a kill was worth.
   */
  award(points, meta = {}) {
    if (!points) return;
    this.score += points + (meta.streak > 1 ? TUNING.multikillBonus * (meta.streak - 1) : 0);
    this._checkEarned();
    this._emitProgress();
  }

  _checkEarned() {
    while (this.rung < STREAKS.length && this.score >= STREAKS[this.rung].cost) {
      const def = STREAKS[this.rung++];
      this.grant(def.id, { source: 'earned' });
    }
  }

  /** The next rung, and how far along it the player is. */
  get next() {
    return this.rung < STREAKS.length ? STREAKS[this.rung] : null;
  }

  /* ---------------------------------- tray --------------------------------- */

  /** Put a streak in the tray. Used by earning, by care packages, and by tests. */
  grant(id, meta = {}) {
    const def = STREAK_BY_ID.get(id);
    if (!def) return false;
    const ev = this.ctx.events;

    if (this.tray.length >= TUNING.traySize) {
      ev.emit('streak:refused', { id: def.id, name: def.name, reason: 'TRAY FULL' });
      ev.emit('audio:play', { id: 'dryfire', volume: 0.7 });
      return false;
    }

    this.tray.push(def);
    this.earnedTotal++;
    if (this.tray.length === 1) this.selected = 0;

    ev.emit('streak:earned', {
      id: def.id,
      name: def.name,
      blurb: def.blurb,
      tier: def.tier,
      cost: def.cost,
      source: meta.source ?? 'earned',
    });
    // The moment has to be loud: a rising two-note stab plus a HUD flash.
    ev.emit('audio:play', { id: 'ui_kill', volume: 0.85, pitch: 0.8 });
    ev.emit('audio:play', { id: 'ui_headshot', volume: 0.7, pitch: 1.25, delay: 0.14 });
    this.ctx.postfx?.flash?.(def.tier === 'ultimate' ? 0.3 : 0.1, 0.28);
    this._emitTray();
    return true;
  }

  /**
   * A deploy asked for by the HUD rather than by a keypress.
   *
   * The tray was keyboard-only: `4` to cycle, `5` to deploy, and nothing at all on
   * a touchscreen — every streak the player earned on a phone was unreachable.
   * Tapping the slot you want is the obvious gesture and needs no twelfth button on
   * an overlay that is already crowded, so the HUD sends this and the system stays
   * the only thing that decides whether the streak actually goes out.
   *
   * Selecting and deploying in one call on purpose: on a keyboard those are two
   * steps because cycling is how you *see* the choice, but a tap has already named
   * the one it means.
   */
  request({ index = null, id = null } = {}) {
    if (index != null) {
      if (index < 0 || index >= this.tray.length) return false;
      this.selected = index;
      this._emitTray();
    }
    return this.deploy(id);
  }

  cycle(dir = 1) {
    if (this.tray.length < 2) return;
    this.selected = (this.selected + dir + this.tray.length) % this.tray.length;
    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.45 });
    this._emitTray();
  }

  select(i) {
    if (i < 0 || i >= this.tray.length || i === this.selected) return;
    this.selected = i;
    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.45 });
    this._emitTray();
  }

  /**
   * Spend the selected streak. Returns true if it actually went out — a streak
   * that refuses to deploy (no ground under the player, player already dead) is
   * not consumed.
   */
  deploy(id = null) {
    const idx = id ? this.tray.findIndex((d) => d.id === id) : this.selected;
    const def = this.tray[idx];
    if (!def) return false;
    if (this.takeover) return false;

    const p = this.ctx.player;
    if (!p || p.dead) return false;

    let impl = this.impls.get(def.id);
    if (!impl) {
      impl = new def.Impl(this.ctx, this);
      this.impls.set(def.id, impl);
    }
    // Re-deploying a type that is already up restarts it rather than stacking.
    const running = this.active.findIndex((a) => a.id === def.id);
    if (running !== -1) {
      impl.end?.();
      this.active.splice(running, 1);
    }

    let ok = false;
    try {
      ok = impl.deploy() !== false;
    } catch (err) {
      console.error(`[scorestreaks] "${def.id}" failed to deploy`, err);
      ok = false;
    }
    if (!ok) {
      this.ctx.events.emit('audio:play', { id: 'dryfire', volume: 0.7 });
      return false;
    }

    this.tray.splice(idx, 1);
    this.selected = Math.min(this.selected, Math.max(0, this.tray.length - 1));
    this.active.push({ id: def.id, def, impl });
    this.ctx.events.emit('streak:deploy', { id: def.id, name: def.name, tier: def.tier });
    this._emitTray();
    return true;
  }

  _retire(i) {
    const a = this.active[i];
    this.active.splice(i, 1);
    this.ctx.events.emit('streak:end', { id: a.id, name: a.def.name });
    if (this.takeover === a.id) this.setTakeover(false, a.id);
  }

  /* --------------------------------- frame --------------------------------- */

  update(dt, elapsed) {
    const t0 = performance.now();

    this._input();

    if (this.active.length) {
      for (let i = this.active.length - 1; i >= 0; i--) {
        const a = this.active[i];
        let alive = true;
        try {
          alive = a.impl.update(dt, elapsed) !== false;
        } catch (err) {
          console.error(`[scorestreaks] "${a.id}" threw during update`, err);
          alive = false;
        }
        if (!alive) this._retire(i);
      }
    }

    this._intel(dt);
    updateTracers(dt);

    const ms = performance.now() - t0;
    this.ms += (ms - this.ms) * 0.06;
    if (ms > this.msPeak) this.msPeak = ms;
  }

  /** Camera takeover only. Registered after the camera rig, on the same phase. */
  lateUpdate(dt, elapsed) {
    if (!this.takeover) return;
    const a = this.active.find((x) => x.id === this.takeover);
    a?.impl.lateUpdate?.(dt, elapsed);
  }

  _bindRequests() {
    this.ctx.events.on('streak:request', (p) => this.request(p ?? {}));
  }

  _input() {
    const input = this.ctx.input;
    const pressed = input?.pressedThisFrame;
    if (!pressed || pressed.size === 0) return;

    if (pressed.has(TUNING.keyDeploy)) {
      // 5 deploys when something is selected; with a single item in the tray it is
      // also the direct-select key, so a one-item tray needs exactly one keypress.
      this.deploy();
      return;
    }
    if (pressed.has(TUNING.keyCycle)) this.cycle(1);
  }

  /* ---------------------------------- intel -------------------------------- */

  /**
   * Reveal every hostile. A finite radius takes a snapshot (a ping tells you where
   * they *were*); an infinite one tracks continuously (an aircraft tells you where
   * they *are*).
   */
  revealAll(duration, mode = 'pulse', radius = Infinity) {
    this._revealTimer = Math.max(this._revealTimer, duration);
    this._revealMode = mode;
    if (Number.isFinite(radius)) {
      this._continuous = false;
      this._snapshot.clear();
      const p = this.ctx.player;
      const list = livingEnemies(this.ctx, this._scratch);
      for (const e of list) {
        if (!p || e.position.distanceTo(p.position) <= radius) this._snapshot.add(e.id ?? e);
      }
    } else {
      this._continuous = true;
    }
  }

  /** Paint one contact, the way a spotter would. */
  revealActor(actor, duration) {
    const id = actor?.id ?? actor;
    if (id == null) return;
    const cur = this.perActor.get(id) ?? 0;
    if (duration > cur) this.perActor.set(id, duration);
  }

  _intel(dt) {
    const intel = this.ctx.intel;

    if (this._revealTimer > 0) {
      this._revealTimer -= dt;
      if (this._revealTimer <= 0) {
        this._revealMode = 'none';
        this._continuous = false;
        this._snapshot.clear();
      }
    }
    if (this.perActor.size) {
      for (const [id, t] of this.perActor) {
        const n = t - dt;
        if (n <= 0) this.perActor.delete(id);
        else this.perActor.set(id, n);
      }
    }

    const on = this._revealTimer > 0;
    const wasOn = intel.revealAll || intel.ids.size > 0;
    intel.revealAll = on && this._continuous;
    intel.mode = on ? this._revealMode : 'none';
    intel.remaining = Math.max(0, this._revealTimer);

    intel.ids.clear();
    if (on && !this._continuous) for (const id of this._snapshot) intel.ids.add(id);
    for (const id of this.perActor.keys()) intel.ids.add(id);

    const nowOn = intel.revealAll || intel.ids.size > 0;
    if (nowOn !== wasOn) {
      this.ctx.events.emit('intel:reveal', {
        active: nowOn,
        mode: intel.mode,
        remaining: intel.remaining,
      });
    }

    this._drawMarkers(nowOn);
  }

  /** Diamonds over painted contacts, drawn through walls. One draw call. */
  _drawMarkers(on) {
    // Idle path: no intel, none last frame either — do not even build the layer.
    if (!on && !this._markersOn) return;
    this._markersOn = on;

    const layer = spriteLayer(this.ctx, 'threat', {
      count: TUNING.markerMax,
      size: TUNING.markerSize,
      color: 0xffffff,
      additive: false,
      depthTest: false,
    });
    layer.begin();
    if (on) {
      const list = livingEnemies(this.ctx, this._scratch);
      const all = this.ctx.intel.revealAll;
      const ids = this.ctx.intel.ids;
      for (const e of list) {
        if (!all && !ids.has(e.id ?? e)) continue;
        layer.add(e.position.x, e.position.y + TUNING.markerHeight, e.position.z, 0.92, 1);
      }
    }
    layer.end();
  }

  /* --------------------------- streak-facing hooks -------------------------- */

  /** A contextual line of text on the HUD (care package claim prompt). */
  setPrompt(text, progress = 0) {
    if (text === this._prompt && !progress) return;
    this._prompt = text;
    this.ctx.events.emit('streak:prompt', { text, progress: clamp(progress, 0, 1) });
  }

  /** Per-frame readout for a streak that owns the screen. */
  pushHud(data) {
    this.ctx.events.emit('streak:hud', data);
  }

  /**
   * Enter or leave a full-screen takeover. Fades the normal HUD out through the
   * mechanism the pause menu already uses, rather than reaching into its widgets.
   */
  setTakeover(on, id) {
    this.takeover = on ? id : null;
    const hudRoot = this.ctx.hud?.root;
    if (hudRoot) hudRoot.classList.toggle('is-occluded', !!on);
    this.ctx.events.emit('streak:takeover', { active: !!on, id });
    if (!on) this.ctx.events.emit('streak:hud', null);
  }

  /* --------------------------------- events -------------------------------- */

  _emitProgress() {
    const next = this.next;
    this.ctx.events.emit('streak:progress', {
      score: this.score,
      next: next ? { id: next.id, name: next.name, cost: next.cost } : null,
      prev: this.rung > 0 ? STREAKS[this.rung - 1].cost : 0,
      ratio: next
        ? clamp(
          (this.score - (this.rung > 0 ? STREAKS[this.rung - 1].cost : 0)) /
              Math.max(1, next.cost - (this.rung > 0 ? STREAKS[this.rung - 1].cost : 0)),
          0,
          1
        )
        : 1,
    });
  }

  _emitTray() {
    this.ctx.events.emit('streak:tray', {
      items: this.tray.map((d) => ({ id: d.id, name: d.name, tier: d.tier, cost: d.cost })),
      selected: this.selected,
      max: TUNING.traySize,
    });
  }

  /* ------------------------------- test hooks ------------------------------- */

  /**
   * Drives every part of the system without grinding kills. Additive only — it
   * calls the same entry points the game does.
   */
  _exposeTestHooks() {
    window.__streaktest = {
      /** Add streak score, exactly as a kill would. */
      award: (score = 500, streak = 1) => {
        this.award(score, { streak });
        return this.score;
      },
      /** Put a streak straight into the tray. */
      grant: (id = 'uav') => this.grant(id, { source: 'test' }),
      /** Deploy immediately, granting first if the tray does not have it. */
      deploy: (id = null) => {
        if (id && !this.tray.some((d) => d.id === id)) this.grant(id, { source: 'test' });
        return this.deploy(id);
      },
      /** Cycle / select. */
      cycle: (d = 1) => this.cycle(d),
      /** Wipe score, tray and everything on the map. */
      reset: () => {
        for (let i = this.active.length - 1; i >= 0; i--) {
          this.active[i].impl.end?.();
          this._retire(i);
        }
        this.score = 0;
        this.rung = 0;
        this.tray.length = 0;
        this.selected = 0;
        this._revealTimer = 0;
        this._continuous = false;
        this._snapshot.clear();
        this.perActor.clear();
        this.msPeak = 0;
        this._emitProgress();
        this._emitTray();
        return true;
      },
      /** Everything the ladder contains, for the record. */
      ladder: () => STREAKS.map((s) => ({ id: s.id, name: s.name, cost: s.cost, tier: s.tier })),
      /** Cost, per frame, in milliseconds. */
      cost: () => ({ ms: +this.ms.toFixed(4), peak: +this.msPeak.toFixed(4), active: this.active.length }),
      state: () => ({
        score: this.score,
        tray: this.tray.map((d) => d.id),
        selected: this.selected,
        active: this.active.map((a) => a.id),
        intel: { ...this.ctx.intel, ids: [...this.ctx.intel.ids] },
      }),
      /** Spawn a hostile in front of the player, so a turret has something to shoot. */
      bait: (n = 3, dist = 14) => {
        const ai = this.ctx.ai;
        const p = this.ctx.player;
        if (!ai?.spawnAt && !ai?._spawn) return 0;
        let made = 0;
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          const v = new THREE.Vector3(
            p.position.x - Math.sin(p.yaw) * dist + Math.cos(a) * 4,
            p.position.y,
            p.position.z - Math.cos(p.yaw) * dist + Math.sin(a) * 4
          );
          try {
            (ai._spawn ?? ai.spawnAt).call(ai, v);
            made++;
          } catch { /* the AI module owns spawn validity; a refusal is fine */ }
        }
        return made;
      },
      /**
       * Set up and fire one streak with hostiles to use it on, then keep the
       * scenario running. Also reachable as `?streak=<id>` on the URL, which is
       * the only way to inspect a streak in a session that hot-reloads under you.
       */
      demo: (id = 'napalm', enemies = 6) => {
        // Hand control to the game the same way a keyboard player does, so the
        // start screen is not sitting on top of whatever we are looking at.
        this.ctx.input?.engage?.();
        window.__streaktest.reset();
        window.__streaktest.bait(enemies, 26);
        return window.__streaktest.deploy(id);
      },
      sys: this,
    };

    // `?streak=<id>` keeps that streak permanently deployed with hostiles to use
    // it on. Intended for looking at one asset in isolation; it re-arms itself, so
    // a hot reload mid-inspection costs nothing.
    const q = new URLSearchParams(location.search);
    const wanted = q.get('streak');
    if (wanted && STREAK_BY_ID.has(wanted)) {
      const bots = Number(q.get('bots') ?? 6);
      const rearm = () => {
        if (this.active.some((a) => a.id === wanted)) return;
        if ((this.ctx.ai?.enemies?.filter((e) => !e.dead).length ?? 0) < bots) {
          window.__streaktest.bait(bots, 26);
        }
        this.ctx.input?.engage?.();
        this.grant(wanted, { source: 'test' });
        this.deploy(wanted);
      };
      setTimeout(rearm, 900);
      this._demoTimer = setInterval(rearm, 2500);
    }
  }

  dispose() {
    clearInterval(this._demoTimer);
    for (const off of this._offs) off();
    this._unregSim?.();
    this._unregLate?.();
    for (const impl of this.impls.values()) impl.dispose?.();
    this.impls.clear();
    this.active.length = 0;
    disposeShared();
    if (window.__streaktest?.sys === this) delete window.__streaktest;
  }
}
